import { Container, Section, Grid } from '../layout';
import { Card, Icon } from '../ui';
import { firmData } from '../../data/firmData';

export const AboutSection = () => {
  const statistics = [
    {
      icon: 'calendar',
      value: `${firmData.statistics.yearsOfPractice}+`,
      label: 'Years of Practice',
      description: 'Dedicated legal expertise and client service'
    },
    {
      icon: 'briefcase',
      value: `${firmData.statistics.mattersHandled}+`,
      label: 'Matters Handled',
      description: 'Successfully resolved legal cases and transactions'
    },
    {
      icon: 'users',
      value: `${firmData.statistics.clientEngagements}+`,
      label: 'Client Engagements',
      description: 'Trusted partnerships with diverse clientele'
    },
    {
      icon: 'star',
      value: `${firmData.statistics.legalAssociates}+`,
      label: 'Legal Associates',
      description: 'Experienced team of legal professionals'
    }
  ];

  return (
    <Section id="about" padding="xl" className="bg-background-light">
      <Container>
        <div className="text-center mb-16">
          <h2 className="heading-lg text-primary-navy mb-6">
            About VC Law Associates
          </h2>
          <p className="body-lg text-text-medium max-w-3xl mx-auto">
            Established in {firmData.foundingYear}, VC Law Associates has grown to become a trusted name in comprehensive legal solutions. 
            Our commitment to excellence, integrity, and client-focused service has earned us recognition across diverse practice areas.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          {/* Firm Overview */}
          <div>
            <h3 className="heading-md text-primary-navy mb-6">Our Foundation</h3>
            <div className="space-y-4">
              <p className="body-base text-text-medium">
                Founded with a vision to provide exceptional legal services, VC Law Associates combines traditional legal expertise 
                with modern approaches to deliver comprehensive solutions for our clients' diverse needs.
              </p>
              <p className="body-base text-text-medium">
                Our practice spans across corporate law, commercial litigation, real estate, intellectual property, employment law, 
                and regulatory compliance, ensuring that we can address the full spectrum of legal challenges faced by businesses and individuals.
              </p>
              <p className="body-base text-text-medium">
                We pride ourselves on building long-term relationships with our clients, understanding their unique requirements, 
                and providing strategic legal counsel that supports their growth and success.
              </p>
            </div>

            <div className="mt-8 p-6 bg-background-white rounded-lg border-l-4 border-accent-gold">
              <h4 className="font-semibold text-primary-navy mb-2">Our Mission</h4>
              <p className="body-base text-text-medium">
                To deliver exceptional legal services with unwavering integrity, innovative solutions, and a deep commitment 
                to our clients' success while maintaining the highest standards of professional excellence.
              </p>
            </div>
          </div>

          {/* Statistics Grid */}
          <div>
            <h3 className="heading-md text-primary-navy mb-6">Our Track Record</h3>
            <Grid cols={{ default: 1, sm: 2 }} gap="md">
              {statistics.map((stat, index) => (
                <Card key={index} variant="hover" className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-accent-gold/10 rounded-full flex items-center justify-center">
                      <Icon name={stat.icon as any} size="lg" className="text-accent-gold" />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-primary-navy mb-2">
                    {stat.value}
                  </div>
                  <div className="font-semibold text-text-dark mb-2">
                    {stat.label}
                  </div>
                  <p className="body-sm text-text-medium">
                    {stat.description}
                  </p>
                </Card>
              ))}
            </Grid>
          </div>
        </div>

        {/* Core Values */}
        <div className="bg-background-white rounded-lg p-8">
          <h3 className="heading-md text-primary-navy mb-8 text-center">Our Core Values</h3>
          <Grid cols={{ default: 1, md: 3 }} gap="lg">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-navy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="scale" size="xl" className="text-primary-navy" />
              </div>
              <h4 className="font-semibold text-primary-navy mb-3">Integrity</h4>
              <p className="body-sm text-text-medium">
                Upholding the highest ethical standards in all our professional dealings and client relationships.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-navy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="lightbulb" size="xl" className="text-primary-navy" />
              </div>
              <h4 className="font-semibold text-primary-navy mb-3">Excellence</h4>
              <p className="body-sm text-text-medium">
                Delivering superior legal services through continuous learning, innovation, and attention to detail.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-navy/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="users" size="xl" className="text-primary-navy" />
              </div>
              <h4 className="font-semibold text-primary-navy mb-3">Client Focus</h4>
              <p className="body-sm text-text-medium">
                Prioritizing our clients' needs and objectives in every legal strategy and solution we provide.
              </p>
            </div>
          </Grid>
        </div>
      </Container>
    </Section>
  );
};